/**
 * Main Calendar Component
 * 
 * Wrapper for React Big Calendar with custom components and configuration
 */

import React, { useMemo, useCallback } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import type { View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import type { CalendarProps, CalendarEvent, CalendarView } from '../../types/calendar';
import { CalendarToolbar } from './CalendarToolbar';
import { CalendarEvent as CustomEventComponent } from './CalendarEvent';
import { JobEventCard } from './JobEventCard';
import { useCalendar } from '../../hooks/useCalendar';
import { useTimeSlotDynamicHeights } from '../../hooks/useTimeSlotDynamicHeights';
import { useCalendarRowHeights } from '../../hooks/useCalendarRowHeights';
import { CALENDAR_CONFIG } from './CalendarConfig';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { toggleEventExpanded, selectExpandedEventIds } from '../../store/slices/calendarUISlice';

// Import CSS for React Big Calendar
import './calendar-custom.css';
import './JobEventCard.css';

// Configure date-fns localizer
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Create Drag and Drop Calendar (HOC typings default to object events; we use CalendarEvent throughout)
const DragAndDropCalendar = withDragAndDrop(BigCalendar);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- RBC withDragAndDrop loses generic TEvent in types
const DnDCalendar = DragAndDropCalendar as React.ComponentType<any>;

/**
 * Custom Event Wrapper Component
 * Renders job event card or generic event component with expand/collapse support
 */
interface EventWrapperProps {
  event: CalendarEvent;
  onClick: (event: CalendarEvent) => void;
  view?: string;
  isExpanded: boolean;
  onToggleExpand: (eventId: string) => void;
  onMoveUp?: (eventId: string) => void;
  onMoveDown?: (eventId: string) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}

const EventWrapper = ({ event, onClick, view, isExpanded, onToggleExpand, onMoveUp, onMoveDown, canMoveUp, canMoveDown }: EventWrapperProps) => {
  // Check if this is a job event (has jobId in metadata)
  const isJobEvent = event.notes?.jobId;
  
  // Use compact mode for week/day views (but still allow expand)
  const isCompact = false; // Removed compact mode per requirements
  
  if (isJobEvent) {
    return (
      <JobEventCard
        event={event}
        onClick={onClick}
        compact={isCompact}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        view={view as 'month' | 'week' | 'day' | 'agenda'}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
      />
    );
  }
  
  return <CustomEventComponent event={event} onClick={onClick} />;
};

/**
 * Main Calendar Component
 */
export const Calendar: React.FC<CalendarProps> = ({
  events: externalEvents,
  defaultView = 'month',
  onEventClick,
  onDateClick,
  onViewChange,
  onEventDrop,
  onDragOver,
  onDateChange,
  onMoveUp,
  onMoveDown,
  className = '',
}) => {
  const dispatch = useAppDispatch();
  const expandedEventIds = useAppSelector(selectExpandedEventIds);
  
  const {
    currentDate,
    currentView,
    events: internalEvents,
    navigateNext,
    navigatePrevious,
    navigateToday,
    setDate,
    setView,
    selectEvent,
  } = useCalendar(defaultView);

  // Use external events if provided, otherwise use internal state
  const events = externalEvents || internalEvents;

  // Adjust time slot heights for week/day views
  useTimeSlotDynamicHeights(
    events,
    expandedEventIds,
    currentView
  );
  useCalendarRowHeights(currentDate, events, expandedEventIds, currentView);

  // Handle expand/collapse toggle
  const handleToggleExpand = useCallback((eventId: string) => {
    dispatch(toggleEventExpanded(eventId));
  }, [dispatch]);

  // Handle view change
  const handleViewChange = useCallback((view: CalendarView) => {
    setView(view);
    onViewChange?.(view);
  }, [setView, onViewChange]);

  // Handle navigation
  const handleNavigate = useCallback((action: 'PREV' | 'NEXT' | 'TODAY') => {
    let newDate: Date;
    const currentDateValue = currentDate;
    
    switch (action) {
      case 'PREV':
        navigatePrevious();
        // Calculate previous month/week/day based on current view
        if (currentView === 'month') {
          newDate = new Date(currentDateValue.getFullYear(), currentDateValue.getMonth() - 1, 1);
        } else if (currentView === 'week') {
          newDate = new Date(currentDateValue.getTime() - 7 * 24 * 60 * 60 * 1000);
        } else {
          newDate = new Date(currentDateValue.getTime() - 24 * 60 * 60 * 1000);
        }
        onDateChange?.(newDate);
        break;
      case 'NEXT':
        navigateNext();
        // Calculate next month/week/day based on current view
        if (currentView === 'month') {
          newDate = new Date(currentDateValue.getFullYear(), currentDateValue.getMonth() + 1, 1);
        } else if (currentView === 'week') {
          newDate = new Date(currentDateValue.getTime() + 7 * 24 * 60 * 60 * 1000);
        } else {
          newDate = new Date(currentDateValue.getTime() + 24 * 60 * 60 * 1000);
        }
        onDateChange?.(newDate);
        break;
      case 'TODAY':
        navigateToday();
        onDateChange?.(new Date());
        break;
    }
  }, [navigatePrevious, navigateNext, navigateToday, currentDate, currentView, onDateChange]);

  // Handle event selection
  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    selectEvent(event);
    onEventClick?.(event);
  }, [selectEvent, onEventClick]);

  // Handle date selection
  const handleSelectSlot = useCallback((slotInfo: { start: Date; end: Date }) => {
    onDateClick?.(slotInfo.start);
  }, [onDateClick]);

  // Handle date navigation from calendar
  const handleNavigateCalendar = useCallback((date: Date) => {
    setDate(date);
    onDateChange?.(date);
  }, [setDate, onDateChange]);

  // Handle event drag and drop
  const handleEventDrop = useCallback((args: { event: CalendarEvent; start: Date; end: Date; isAllDay?: boolean }) => {
    const { event, start, isAllDay } = args;
    // Force end date to be same as start (single day event)
    const singleDayEnd = start;
    
    // Pass additional context about the drop
    const dropInfo = {
      event,
      start,
      end: singleDayEnd,
      view: currentView,
      isAllDay: isAllDay ?? false,
    };
    
    onEventDrop?.(event, start, singleDayEnd, dropInfo);
  }, [onEventDrop, currentView]);
  
  // Handle event resize (prevent it, keep as single day)
  const handleEventResize = useCallback((args: { event: CalendarEvent; start: Date; end: Date }) => {
    const { event, start } = args;
    // Keep as single day
    onEventDrop?.(event, start, start);
  }, [onEventDrop]);

  // Event style getter
  // Note: JobEventCard handles its own styling, so we keep this minimal
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    void event;
    return {
      style: {
        // Let JobEventCard handle colors and styling
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius: '4px',
        opacity: 1,
        display: 'block',
        padding: 0,
      },
    };
  }, []);

  // Custom toolbar
  const CustomToolbar = useCallback(() => {
    return (
      <CalendarToolbar
        currentDate={currentDate}
        currentView={currentView}
        onNavigate={handleNavigate}
        onViewChange={handleViewChange}
        views={CALENDAR_CONFIG.views}
      />
    );
  }, [currentDate, currentView, handleNavigate, handleViewChange]);

  // Calendar components configuration
  const components = useMemo(() => ({
    toolbar: CustomToolbar,
    event: ({ event }: { event: CalendarEvent }) => {
      const isExpanded = expandedEventIds.includes(event.id);
      
      // Calculate reorder capabilities for week/day views
      const sameDayEvents = events
        .filter(e => format(e.start, 'yyyy-MM-dd') === format(event.start, 'yyyy-MM-dd'))
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      const currentIndex = sameDayEvents.findIndex(e => e.id === event.id);
      const canMoveUp = currentIndex > 0;
      const canMoveDown = currentIndex < sameDayEvents.length - 1;
      
      return (
        <EventWrapper
          event={event}
          onClick={handleSelectEvent}
          view={currentView}
          isExpanded={isExpanded}
          onToggleExpand={handleToggleExpand}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
        />
      );
    },
    month: {
      // Custom month event wrapper to prevent RBC from hiding events
      event: ({ event }: { event: CalendarEvent }) => {
        const isExpanded = expandedEventIds.includes(event.id);
        
        // Calculate reorder capabilities for week/day views
        const sameDayEvents = events
          .filter(e => format(e.start, 'yyyy-MM-dd') === format(event.start, 'yyyy-MM-dd'))
          .sort((a, b) => a.start.getTime() - b.start.getTime());
        const currentIndex = sameDayEvents.findIndex(e => e.id === event.id);
        const canMoveUp = currentIndex > 0;
        const canMoveDown = currentIndex < sameDayEvents.length - 1;
        
        return (
          <EventWrapper
            event={event}
            onClick={handleSelectEvent}
            view={currentView}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            canMoveUp={canMoveUp}
            canMoveDown={canMoveDown}
          />
        );
      },
    },
  }), [CustomToolbar, handleSelectEvent, currentView, expandedEventIds, handleToggleExpand, events, onMoveUp, onMoveDown]);

  // Formats configuration
  const formats = useMemo(() => ({
    dayFormat: CALENDAR_CONFIG.formats.dayFormat,
    weekdayFormat: CALENDAR_CONFIG.formats.weekdayFormat,
    monthHeaderFormat: CALENDAR_CONFIG.formats.monthHeaderFormat,
    dayHeaderFormat: CALENDAR_CONFIG.formats.dayHeaderFormat,
    agendaDateFormat: CALENDAR_CONFIG.formats.agendaDateFormat,
    agendaTimeFormat: CALENDAR_CONFIG.formats.agendaTimeFormat,
    timeGutterFormat: CALENDAR_CONFIG.formats.timeGutterFormat,
    // Hide time ranges in event display
    eventTimeRangeFormat: () => '', // Return empty string to hide time
    selectRangeFormat: () => '', // Hide time in selection
  }), []);

  const isMonthView = currentView === 'month';

  return (
    <div className={`calendar-container ${isMonthView ? '' : 'h-full'} flex flex-col ${className}`}>
      {/* React Big Calendar with Drag and Drop */}
      <div className={isMonthView ? 'bg-white rounded-lg shadow-md' : 'flex-1 bg-white rounded-lg shadow-md overflow-hidden'}>
        <DnDCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          allDayAccessor={() => false}
          style={{ height: isMonthView ? 'auto' : '100%' }}
          view={currentView as View}
          onView={handleViewChange as (view: View) => void}
          date={currentDate}
          onNavigate={handleNavigateCalendar}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onEventDrop={handleEventDrop}
          onEventResize={handleEventResize}
          onDragOver={onDragOver}
          draggableAccessor={() => true}
          resizable={false}
          selectable={false}
          showAllEvents={CALENDAR_CONFIG.showAllEventsInMonth}
          popup={false}
          doShowMoreDrillDown={false}
          dayLayoutAlgorithm="overlap"
          showMultiDayTimes={false}
          eventPropGetter={eventStyleGetter}
          components={components}
          formats={formats}
          step={CALENDAR_CONFIG.step}
          timeslots={CALENDAR_CONFIG.timeslots}
          scrollToTime={CALENDAR_CONFIG.scrollToTime}
          min={CALENDAR_CONFIG.min}
          max={CALENDAR_CONFIG.max}
        />
      </div>

      {/* No modal here - parent will handle it */}
    </div>
  );
};

export default Calendar;

